/**
 * The gesture registration pad.
 *
 * Pointer Events, so a trackpad, a mouse and a stylus all work with the same
 * code path. Lifting the pointer ends a stroke rather than the whole gesture,
 * which is what lets a sigil be several strokes — and stroke order is part of
 * what the matcher checks.
 */
import { useEffect, useRef } from 'react';
import type { GesturePoint } from '@spell/shared-types';
import { GestureRecorder } from '../gesture/capture';

export interface GesturePadProps {
  label: string;
  color?: string;
  disabled?: boolean;
  /** Fired when the player finishes a registration (button "이 동작 저장"). */
  onCommit?: (points: GesturePoint[]) => void;
  /** Live stroke feedback for the parent (e.g. enabling the save button). */
  onChange?: (pointCount: number, strokeCount: number) => void;
  /** A previously registered template to draw as a ghost. */
  ghost?: { x: number; y: number; s: number }[] | null;
  /** Exposes the recorder so a parent can read/reset it. */
  recorderRef?: (recorder: GestureRecorder) => void;
}

export function GesturePad({
  label,
  color = '#8fd8ff',
  disabled,
  onChange,
  ghost,
  recorderRef,
}: GesturePadProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const recorder = useRef(new GestureRecorder());
  const drawing = useRef(false);
  const raf = useRef(0);

  useEffect(() => {
    recorderRef?.(recorder.current);
  }, [recorderRef]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);

    const toLocal = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      // Normalise to roughly [-1, 1] with the pad centre at the origin, so the
      // captured shape is directly comparable to an in-combat stroke.
      return {
        x: ((event.clientX - rect.left) / size) * 2 - (rect.width / size),
        y: ((event.clientY - rect.top) / size) * 2 - (rect.height / size),
      };
    };

    const onPointerDown = (event: PointerEvent) => {
      if (disabled) return;
      event.preventDefault();
      host.setPointerCapture(event.pointerId);
      if (!recorder.current.active) recorder.current.begin();
      drawing.current = true;
      const p = toLocal(event);
      recorder.current.addAbsolute(p.x, p.y);
      onChange?.(recorder.current.points.length, recorder.current.strokeCount);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!drawing.current) return;
      const p = toLocal(event);
      recorder.current.addAbsolute(p.x, p.y);
      onChange?.(recorder.current.points.length, recorder.current.strokeCount);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!drawing.current) return;
      drawing.current = false;
      try {
        host.releasePointerCapture(event.pointerId);
      } catch {
        /* pointer already released */
      }
      recorder.current.endStroke();
      onChange?.(recorder.current.points.length, recorder.current.strokeCount);
    };

    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerup', onPointerUp);
    host.addEventListener('pointercancel', onPointerUp);
    host.addEventListener('pointerleave', onPointerUp);

    const render = () => {
      raf.current = requestAnimationFrame(render);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const size = Math.max(w, h);
      const project = (x: number, y: number): [number, number] => [
        (x + w / size) * (size / 2),
        (y + h / size) * (size / 2),
      ];

      // Guide grid.
      ctx.strokeStyle = 'rgba(122,140,200,0.12)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo((w / 4) * i, 0);
        ctx.lineTo((w / 4) * i, h);
        ctx.moveTo(0, (h / 4) * i);
        ctx.lineTo(w, (h / 4) * i);
        ctx.stroke();
      }

      // Ghost of the first registration.
      if (ghost && ghost.length > 1) {
        ctx.strokeStyle = 'rgba(217,188,118,0.35)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        let lastStroke = ghost[0].s;
        ghost.forEach((p, i) => {
          const [px, py] = project(p.x, p.y);
          if (i === 0 || p.s !== lastStroke) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
          lastStroke = p.s;
        });
        ctx.stroke();
      }

      // Live stroke.
      const pts = recorder.current.points;
      if (pts.length > 1) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const pass of [
          { width: 14, alpha: 0.16 },
          { width: 6, alpha: 0.45 },
          { width: 2.4, alpha: 1 },
        ]) {
          ctx.strokeStyle = color;
          ctx.globalAlpha = pass.alpha;
          ctx.lineWidth = pass.width;
          ctx.beginPath();
          let last = pts[0].s;
          pts.forEach((p, i) => {
            const [px, py] = project(p.x, p.y);
            if (i === 0 || p.s !== last) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
            last = p.s;
          });
          ctx.stroke();
        }
        ctx.globalAlpha = 1;

        // Start marker, so stroke direction is legible after the fact.
        const [sx, sy] = project(pts[0].x, pts[0].y);
        ctx.fillStyle = '#6ee7a5';
        ctx.beginPath();
        ctx.arc(sx, sy, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    raf.current = requestAnimationFrame(render);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf.current);
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerup', onPointerUp);
      host.removeEventListener('pointercancel', onPointerUp);
      host.removeEventListener('pointerleave', onPointerUp);
    };
  }, [color, disabled, ghost, onChange]);

  return (
    <div className="gesture-pad" ref={hostRef} data-testid="gesture-pad">
      <canvas className="gesture-pad__canvas" ref={canvasRef} />
      <div className="gesture-pad__label">{label}</div>
      <div className="gesture-pad__status">여러 획으로 그려도 됩니다 · 획 순서와 방향이 중요합니다</div>
    </div>
  );
}
